import React, { useState, useEffect } from 'react';
import './AdminStyles.css';
import { activityService } from '../../services/activityService';
import ImageUpload from './components/ImageUpload';

const ActivitiesManagement = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingActivity, setEditingActivity] = useState(null);
  const [formData, setFormData] = useState({
    slug: '',
    title: '',
    description: '',
    fullDescription: '',
    coverImage: null,
    gallery: [],
    hasPrice: true,
    prices: []
  });
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    loadActivities();
  }, []);

  const loadActivities = async () => {
    try {
      const data = await activityService.getActivities();
      setActivities(data.activities);
    } catch (error) {
      console.error('Ошибка загрузки активностей:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCoverSelect = (file) => {
    setFormData(prev => ({ ...prev, coverImage: file }));
  };

  const handleGallerySelect = (files) => {
    setFormData(prev => ({ ...prev, gallery: files }));
  };

  const handlePriceChange = (index, field, value) => {
    const updatedPrices = [...formData.prices];
    updatedPrices[index] = { ...updatedPrices[index], [field]: value };
    setFormData(prev => ({ ...prev, prices: updatedPrices }));
  };

  const addPriceRow = () => {
    setFormData(prev => ({
      ...prev,
      prices: [...prev.prices, { service: '', description: '', price: '' }]
    }));
  };

  const removePriceRow = (index) => {
    const updatedPrices = formData.prices.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, prices: updatedPrices }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const formDataToSend = new FormData();
    formDataToSend.append('slug', formData.slug);
    formDataToSend.append('title', formData.title);
    formDataToSend.append('description', formData.description);
    formDataToSend.append('fullDescription', formData.fullDescription);
    formDataToSend.append('hasPrice', formData.hasPrice);
    
    if (formData.coverImage instanceof File) {
      formDataToSend.append('coverImage', formData.coverImage);
    }
    
    if (formData.gallery.length > 0) {
      formData.gallery.forEach(file => {
        formDataToSend.append('gallery', file);
      });
    }
    
    formDataToSend.append('prices', JSON.stringify(formData.prices));

    try {
      if (editingActivity) {
        await activityService.updateActivity(editingActivity.id, formDataToSend);
      } else {
        await activityService.createActivity(formDataToSend);
      }
      
      await loadActivities();
      resetForm();
    } catch (error) {
      console.error('Ошибка сохранения активности:', error);
    }
  };

  const handleEdit = (activity) => {
    setEditingActivity(activity);
    setFormData({
      slug: activity.slug,
      title: activity.title,
      description: activity.description,
      fullDescription: activity.fullDescription || activity.description || '',
      coverImage: null,
      gallery: [],
      hasPrice: activity.hasPrice,
      prices: activity.prices || []
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить эту активность?')) {
      try {
        await activityService.deleteActivity(id);
        await loadActivities();
      } catch (error) {
        console.error('Ошибка удаления активности:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      slug: '',
      title: '',
      description: '',
      fullDescription: '',
      coverImage: null,
      gallery: [],
      hasPrice: true,
      prices: []
    });
    setEditingActivity(null);
    setShowForm(false);
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-zа-яё0-9\s]/g, '')
      .replace(/\s+/g, '-');
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData(prev => ({
      ...prev,
      title,
      slug: generateSlug(title)
    }));
  };

  if (loading) {
    return <div className="admin-loading">Загрузка...</div>;
  }

  return (
    <div className="admin-content">
      <div className="admin-header">
        <h2>Управление активностями</h2>
        <button 
          className="admin-button primary"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Отмена' : 'Добавить активность'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="admin-form">
          <h3>{editingActivity ? 'Редактировать активность' : 'Новая активность'}</h3>
          
          <div className="form-group">
            <label>Название</label>
            <input
              type="text"
              value={formData.title}
              onChange={handleTitleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>URL идентификатор (slug)</label>
            <input
              type="text"
              name="slug"
              value={formData.slug}
              onChange={handleInputChange}
              required
            />
            <small>Будет использоваться в URL: /activity/{formData.slug}</small>
          </div>

          <div className="form-group">
            <label>Краткое описание</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="3"
              required
            />
          </div>

          <div className="form-group">
            <label>Полное описание</label>
            <textarea
              name="fullDescription"
              value={formData.fullDescription}
              onChange={handleInputChange}
              rows="6"
            />
          </div>

          <ImageUpload
            label="Обложка"
            onImageSelect={handleCoverSelect}
            currentImage={editingActivity?.coverImage}
          />

          <ImageUpload
            label="Галерея изображений"
            onImageSelect={handleGallerySelect}
            multiple={true}
          />

          <div className="form-group checkbox">
            <label>
              <input
                type="checkbox"
                name="hasPrice"
                checked={formData.hasPrice}
                onChange={handleInputChange}
              />
              Есть прайс-лист
            </label>
          </div>

          {formData.hasPrice && (
            <div className="prices-section">
              <h4>Прайс-лист</h4>
              {formData.prices.map((price, index) => (
                <div key={index} className="price-row">
                  <input
                    type="text"
                    placeholder="Услуга"
                    value={price.service}
                    onChange={(e) => handlePriceChange(index, 'service', e.target.value)}
                    required
                  />
                  <input
                    type="text"
                    placeholder="Описание (опционально)"
                    value={price.description || ''}
                    onChange={(e) => handlePriceChange(index, 'description', e.target.value)}
                  />
                  <input
                    type="text"
                    placeholder="Цена"
                    value={price.price}
                    onChange={(e) => handlePriceChange(index, 'price', e.target.value)}
                    required
                  />
                  <button 
                    type="button" 
                    className="admin-button small delete"
                    onClick={() => removePriceRow(index)}
                  >
                    ×
                  </button>
                </div>
              ))}
              <button 
                type="button" 
                className="admin-button secondary"
                onClick={addPriceRow}
              >
                Добавить цену
              </button>
            </div>
          )}

          <div className="form-actions">
            <button type="submit" className="admin-button primary">
              {editingActivity ? 'Сохранить' : 'Создать'}
            </button>
            <button type="button" className="admin-button secondary" onClick={resetForm}>
              Отмена
            </button>
          </div>
        </form>
      )}

      <div className="admin-list">
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Изображение</th>
              <th>Название</th>
              <th>Slug</th>
              <th>Прайс</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {activities.map(item => (
              <tr key={item.id}>
                <td>{item.id}</td>
                <td>
                  {item.coverImage && (
                    <img src={item.coverImage} alt={item.title} className="table-image" />
                  )}
                </td>
                <td>{item.title}</td>
                <td>{item.slug}</td>
                <td>
                  <span className={`status-badge ${item.hasPrice ? 'active' : 'inactive'}`}>
                    {item.hasPrice ? 'Есть' : 'Нет'}
                  </span>
                </td>
                <td>
                  <button 
                    className="admin-button small edit"
                    onClick={() => handleEdit(item)}
                  >
                    Редактировать
                  </button>
                  <button 
                    className="admin-button small delete"
                    onClick={() => handleDelete(item.id)}
                  >
                    Удалить
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ActivitiesManagement;